<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { getHubStores } from '$lib/stores/hubStores';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import Icon from '$lib/components/Icon/Icon.svelte';
	import Modal from '$lib/components/Modal/Modal.svelte';
	import Size from '$lib/components/Modal/size';
	import PerformancePage from '$lib/components/headerBar/performance/PerformancePage.svelte';
	import PostLink from '../Tile/PostLink.svelte';
	import { getPostContextStore } from '$lib/components/Post/postContextStore';
	import { toggleLike } from '$lib/components/Post/postCommands';

	const { hub, api } = getHubStores();

	const post = getPostContextStore();

	let mouseOverView = false;
	let mouseOverLike = false;
	let mouseOverComment = false;

	const canCommentOrReply = $hub.user_context.perms.posts.contribute;

	const likeAction = async () => {
		try {
			await toggleLike(api, post);
		} catch (e) {
			const type = $post.user_context?.liked ? 'unlike' : 'like';
			toastMessages.danger($_(`post.${type}.failed`), e);
		}
	};

	let isPerformancePageOpened = false;

	const iconAndTextColor = $hub.theme.accessibility ? 'rgb(64, 65, 71)' : 'rgb(123, 125, 122)';
</script>

<!-- eslint-disable svelte/valid-compile -->

{#if $hub.user_context.perms.posts.view_metrics}
	<Modal
		onClose={() => {
			isPerformancePageOpened = false;
		}}
		isOpen={isPerformancePageOpened}
		type={Size.Large}>
		<div slot="content"><PerformancePage post={$post} /></div>
	</Modal>
{/if}
<div class="footer">
	<div class="footerLeft">
		<a
			href={$post.url}
			on:mouseover={() => {
				mouseOverView = true;
			}}
			on:focus={null}
			on:mouseout={() => {
				mouseOverView = false;
			}}
			on:blur={null}
			aria-label={$_('hubs.post.accessibility.tile.footer.view.icon', {
				values: { postTitle: $post.title },
			})}>
			<Icon
				icon={$post.user_context?.viewed || mouseOverView ? 'view' : 'view-uncolored'}
				size={$hub.theme.accessibility ? '4.4' : '2.4'}
				sizeUnit="rem"
				color={iconAndTextColor}
				clickable />
		</a>
		{#if $post.view_count}
			<span class="count" tabindex="0" style="block-size:0;inline-size: 0;opacity:0">
				{$_('hubs.post.accessibility.tile.footer.view.count', {
					values: { postViews: $post.view_count, postTitle: $post.title },
				})}
			</span>
			<span style="color: {iconAndTextColor}">{$post.view_count}</span>
		{/if}
		<button
			type="button"
			on:click={likeAction}
			on:mouseover={() => {
				mouseOverLike = true;
			}}
			on:focus={null}
			on:mouseout={() => {
				mouseOverLike = false;
			}}
			on:blur={null}
			aria-label={$post.user_context?.liked
				? $_('hubs.post.accessibility.tile.footer.unlike.icon', {
						values: { postTitle: $post.title },
				  })
				: $_('hubs.post.accessibility.tile.footer.like.icon', {
						values: { postTitle: $post.title },
				  })}
			><Icon
				icon={$post.user_context?.liked || mouseOverLike ? 'like' : 'like-uncolored'}
				size={$hub.theme.accessibility ? '4.4' : '2.4'}
				sizeUnit="rem"
				color={$post.user_context?.liked || mouseOverLike ? 'red' : `${iconAndTextColor}`}
				className={'clickable'} />
		</button>
		{#if $post.like_count}
			<span class="count" tabindex="0" style="block-size:0;inline-size: 0;opacity:0">
				{$_('hubs.post.accessibility.tile.footer.like.count', {
					values: { postLikes: $post.like_count, postTitle: $post.title },
				})}
			</span>
			<span style="color: {iconAndTextColor}">{$post.like_count}</span>
		{/if}
		{#if canCommentOrReply}
			<span
				on:mouseover={() => {
					mouseOverComment = true;
				}}
				on:focus={null}
				on:mouseout={() => {
					mouseOverComment = false;
				}}
				on:blur={null}>
				<PostLink anchor="contributions">
					<Icon
						icon={$post.user_context?.commented || mouseOverComment
							? 'comment'
							: 'comment-uncolored'}
						color={$post.user_context?.commented || mouseOverComment
							? '#3e88e0'
							: `${iconAndTextColor}`}
						size={$hub.theme.accessibility ? '4.4' : '2.4'}
						sizeUnit="rem"
						clickable />
				</PostLink>
			</span>

			{#if $post.comment_count}
				<span class="count" tabindex="0" style="block-size:0;inline-size: 0;opacity:0">
					{$_('hubs.post.accessibility.tile.footer.comment.count', {
						values: { postComments: $post.comment_count, postTitle: $post.title },
					})}
				</span>
				<span style="color: {iconAndTextColor}">{$post.comment_count}</span>
			{/if}
		{/if}
	</div>
	{#if $hub.user_context.perms.posts.view_metrics}
		<button
			type="button"
			aria-label={$_('hubs.post.accessibility.tile.footer.metrics.icon', {
				values: { postTitle: $post.title },
			})}
			on:click={() => {
				isPerformancePageOpened = true;
			}}>
			<Icon
				icon="bar-graph"
				size={$hub.theme.accessibility ? '4.4' : '2.4'}
				sizeUnit="rem"
				color={iconAndTextColor} />
		</button>
	{/if}
</div>

<style>
	.footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	button {
		margin: 0;
		padding: 0;
		border: none;
		background: none;
	}

	.footerLeft {
		display: flex;
		align-items: center;
		gap: var(--gutterSmall);
	}

	.count {
		font-size: var(--hubPostTileFooterFontSize);
	}

	.count:focus + span {
		outline: -webkit-focus-ring-color auto 1px;
	}
</style>
